/**
 * Compass heading (camera bearing) from the Device Orientation API.
 *
 * Used to aim the compass stamped onto a report photo. Only meaningful for a
 * photo shot right now — a gallery photo's bearing can only come from its EXIF
 * `GPSImgDirection`, which the server reads instead.
 *
 * Accuracy caveat: the camera path hands off to the system camera app via
 * `<input capture>`, so the page is backgrounded while the shutter fires. The
 * reading below is taken when the file comes back, by which time the phone is
 * usually still pointed the same way — but it is an approximation. An exact
 * bearing needs an in-page `getUserMedia` camera where we own the shutter.
 */

/** Safari exposes a true-north heading directly; the spec does not. */
interface CompassOrientationEvent extends DeviceOrientationEvent {
  webkitCompassHeading?: number;
}

/** iOS 13+ gates orientation events behind an explicit, gesture-scoped grant. */
interface PermissionGatedDeviceOrientation {
  requestPermission?: () => Promise<'granted' | 'denied' | 'default'>;
}

const normalize = (deg: number): number => ((deg % 360) + 360) % 360;

/**
 * A reading is a bearing only when it is finite and non-negative.
 *
 * iOS reports "compass unavailable" as `webkitCompassHeading === -1`, and a
 * sensor that answers without a fix can report `NaN`. Both satisfy
 * `typeof … === 'number'`, and both survive {@link normalize} — which maps
 * `-1` onto a confident 359°, one degree off north. The server rejects a
 * negative or non-finite bearing and draws a neutral ring instead, but it
 * never sees these once they have been wrapped into range, so the stamp would
 * claim a direction no instrument measured.
 *
 * An unusable reading is ignored rather than resolved as "no bearing": the
 * sensor often answers `-1` for the first event or two while it settles, so
 * the caller waits out its timeout and a real bearing still has a chance to
 * arrive.
 */
const isBearing = (deg: number): boolean => Number.isFinite(deg) && deg >= 0;

/**
 * Outcome of a permission request. Distinguished so callers can say something
 * true: "denied" is actionable (a Settings toggle), "unsupported" is not, and
 * "timeout" means iOS left the promise pending — which must not leave the UI
 * silent forever.
 */
export type HeadingPermissionStatus = 'granted' | 'denied' | 'unsupported' | 'timeout';

/**
 * Ask iOS for permission to read device orientation.
 *
 * **Must be called from a user gesture** — iOS rejects it otherwise, and
 * without a grant it emits no orientation events at all, so
 * {@link getDeviceHeading} just waits out its timeout and resolves null.
 *
 * Must NOT share a gesture with opening the camera: the native prompt consumes
 * the tap, and the `input.click()` that follows is then no longer
 * gesture-backed, so iOS silently refuses to open the camera at all. It is
 * called when the Camera/Gallery menu *opens* — a gesture that opens no
 * camera — so the camera's own tap stays intact (see `PhotoSourceMenu`).
 *
 * The grant persists for the origin, so it is a one-off per device per tunnel
 * URL. Resolves 'granted' where no permission is needed (Android, desktop).
 */
export async function requestHeadingPermission(
  timeoutMs = 15_000,
): Promise<HeadingPermissionStatus> {
  if (typeof window === 'undefined' || typeof window.DeviceOrientationEvent === 'undefined') {
    return 'unsupported';
  }
  const gated = window.DeviceOrientationEvent as unknown as PermissionGatedDeviceOrientation;
  // No gate (Android, desktop): nothing to request, orientation is readable.
  if (typeof gated.requestPermission !== 'function') return 'granted';
  // Cleared in `finally`: `Promise.race` does not cancel the loser, and callers
  // fire this without awaiting, so an uncleared timer would outlive every call.
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    // Raced against a timeout because a prompt dismissed in some ways leaves
    // the promise pending — without this the caller waits forever and the user
    // sees nothing at all happen.
    const timeout = new Promise<'timeout'>((resolve) => {
      timer = setTimeout(() => resolve('timeout'), timeoutMs);
    });
    const result = await Promise.race([gated.requestPermission(), timeout]);
    if (result === 'timeout') return 'timeout';
    return result === 'granted' ? 'granted' : 'denied';
  } catch {
    // iOS throws when called outside a user gesture.
    return 'denied';
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Resolve the device's compass heading in degrees clockwise from north, or
 * `null` when it can't be determined within `timeoutMs`.
 *
 * Never rejects — a missing sensor, a denied permission, a device that only
 * emits relative orientation, or one that keeps reporting iOS's `-1`
 * "unavailable" sentinel all resolve to `null`, and the caller simply stamps a
 * compass with no bearing (see {@link isBearing}).
 *
 * On iOS this only produces a reading once {@link requestHeadingPermission}
 * has been granted; `PhotoSourceMenu` does that when its menu opens.
 */
export function getDeviceHeading(timeoutMs = 3000): Promise<number | null> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof window.DeviceOrientationEvent === 'undefined') {
      resolve(null);
      return;
    }

    let settled = false;

    const finish = (value: number | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      window.removeEventListener('deviceorientationabsolute', onReading);
      window.removeEventListener('deviceorientation', onReading);
      resolve(value);
    };

    function onReading(event: Event) {
      const e = event as CompassOrientationEvent;
      // iOS reports a true-north heading directly.
      if (typeof e.webkitCompassHeading === 'number' && isBearing(e.webkitCompassHeading)) {
        finish(normalize(e.webkitCompassHeading));
        return;
      }
      // Elsewhere `alpha` is rotation about the z-axis, counter-clockwise from
      // north — so the bearing is its complement. Only trust it when the
      // reading is absolute; a relative one is anchored to wherever the device
      // happened to be when the listener attached.
      if (e.absolute && typeof e.alpha === 'number' && isBearing(e.alpha)) {
        finish(normalize(360 - e.alpha));
      }
    }

    // Declared after `finish` closes over it: `finish` can only run from a
    // listener or this timeout, both of which are after initialisation.
    const timer = setTimeout(() => finish(null), timeoutMs);
    window.addEventListener('deviceorientationabsolute', onReading);
    window.addEventListener('deviceorientation', onReading);
  });
}
